import { ConversationSummaryDTO } from 'models/conversations'
import { InfiniteData } from '@tanstack/react-query'
import { PageableResponse } from 'models/misc'

export type ConversationListPage = PageableResponse<ConversationSummaryDTO[]>

export type ConversationListPages = InfiniteData<ConversationListPage, unknown>

export type ConversationListCacheData = ConversationListPage | ConversationListPages

export const isConversationListPage = (data: unknown): data is ConversationListPage =>
  !!data && typeof data === 'object' && Array.isArray((data as ConversationListPage).content)

export const isConversationListPages = (data: unknown): data is ConversationListPages =>
  !!data && typeof data === 'object' && Array.isArray((data as ConversationListPages).pages) &&
  (data as ConversationListPages).pages.every(isConversationListPage)

export const mapConversationListCache = (
  old: ConversationListCacheData | undefined,
  mapContent: (content: ConversationSummaryDTO[]) => ConversationSummaryDTO[]
): ConversationListCacheData | undefined => {
  if (isConversationListPage(old)) {
    const content = mapContent(old.content)
    return content === old.content ? old : { ...old, content }
  }

  if (isConversationListPages(old)) {
    let changed = false
    const pages = old.pages.map((page) => {
      const content = mapContent(page.content)
      if (content === page.content) return page
      changed = true
      return { ...page, content }
    })
    return changed ? { ...old, pages } : old
  }

  return old
}

export const conversationSummariesOf = (data: unknown): ConversationSummaryDTO[] => {
  if (isConversationListPage(data)) return data.content
  if (isConversationListPages(data)) return data.pages.flatMap((page) => page.content)
  return []
}

export const flattenConversationPages = (data: ConversationListPages | undefined): ConversationSummaryDTO[] => {
  const seen = new Set<string>()
  return conversationSummariesOf(data).filter((conversation) => {
    if (seen.has(conversation.id)) return false
    seen.add(conversation.id)
    return true
  })
}

export const nextConversationPageParam = (lastPage: ConversationListPage): number | undefined =>
  lastPage.last || !lastPage.content.length ? undefined : lastPage.number + 1

export const keepFirstConversationPage = (data: ConversationListPages | undefined): ConversationListPages | undefined =>
  data && data.pages.length > 1 ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data
